// Withdrawal gating, keyed on care_period.status ONLY — the app doesn't reliably surface
// cms_alignment_id, and status==='active' is the reliable CMS-aligned signal (WF12 sets it
// only post-align), so an aligned patient must be unaligned in CMS first.
//
// `blocker` names WHY a visible-but-not-enabled gate is blocked, so the modal can
// explain it instead of the button dying behind a tooltip. The button opens the modal
// whenever `visible`; the modal re-runs this gate itself and refuses to withdraw
// unless `enabled` — the blocked path is a rendered explanation, never a confirm.
// Spec: docs/specs/2026-07-17-patient-withdrawal-offboarding.md §A.
import type { CarePeriodStatus } from './carePeriod';
import { WITHDRAWAL_REQUEST_ON } from './withdrawalRequest';

export type WithdrawBlocker = 'cms_aligned' | 'cms_unalignment_pending' | 'indeterminate';

export interface WithdrawGate {
  visible: boolean;
  /** A full withdrawal (archive + event) may run. */
  enabled: boolean;
  /** What the window offers: the withdrawal, a withdrawal request (CMS still holds the patient), or nothing. */
  mode: 'withdraw' | 'request' | null;
  reason?: string;
  blocker?: WithdrawBlocker;
}

const HIDDEN: WithdrawGate = { visible: false, enabled: false, mode: null };
const ALLOWED: WithdrawGate = { visible: true, enabled: true, mode: 'withdraw' };
const ALIGNED: WithdrawGate = {
  visible: true, enabled: false, mode: null, blocker: 'cms_aligned', reason: 'Unalign in CMS first',
};
// CMS refuses to unalign during its 90-day lock-in, so an aligned patient gets a request instead.
const REQUEST: WithdrawGate = {
  visible: true, enabled: false, mode: 'request', blocker: 'cms_aligned', reason: 'Saves a withdrawal request',
};
const UNALIGNING: WithdrawGate = {
  visible: true, enabled: false, mode: null, blocker: 'cms_unalignment_pending', reason: 'Unalignment in progress',
};
// Default-deny for anything not yet decided (incl. `pending`).
const BLOCKED: WithdrawGate = {
  visible: true, enabled: false, mode: null, blocker: 'indeterminate', reason: 'Not withdrawable in this state',
};

// Exhaustive — adding a CarePeriodStatus without a decision is a compile error.
const GATE: Record<CarePeriodStatus, WithdrawGate> = {
  // already offboarded / ended — nothing to withdraw
  withdrawn: HIDDEN,
  completed: HIDDEN,
  // aligned at CMS — withdrawing would strand the alignment
  active: ALIGNED,
  // $unalign already in flight — wait for the CMS verdict
  pending_unalignment: UNALIGNING,
  // enrolled but not aligned — safe to withdraw
  pending_alignment: ALLOWED,
  pending_eligibility: ALLOWED,
  unaligned: ALLOWED,
  ineligible: ALLOWED,
  pending: BLOCKED,
};

export interface CarePeriodStatusLike {
  status?: string | null;
}

export function canWithdraw(
  carePeriod: CarePeriodStatusLike | null | undefined,
  { requestOn = WITHDRAWAL_REQUEST_ON }: { requestOn?: boolean } = {},
): WithdrawGate {
  if (!carePeriod) return HIDDEN;
  const { status } = carePeriod;
  if (!status) return BLOCKED;
  if (status === 'active' && requestOn) return REQUEST;
  return GATE[status as CarePeriodStatus] ?? BLOCKED;
}

export interface UnalignGate {
  visible: boolean;
  /** True when this is the off-prod escape hatch for a patient stuck outside `active`. */
  testReset: boolean;
}

const NO_UNALIGN: UnalignGate = { visible: false, testReset: false };

/**
 * Whether "See more" → Offboarding offers Unalign, and in which of its two forms.
 *
 * `active` is the real route. The second form is the escape hatch the CMS status card carried:
 * a patient stuck in any other non-terminal state (aligned at CMS but `pending_alignment`
 * locally, say) can be reset off-prod. It stays hidden on production, where unaligning a
 * beneficiary for convenience is not a thing anyone should be able to do by accident.
 */
export function canUnalign(
  carePeriod: CarePeriodStatusLike | null | undefined,
  env: string | undefined,
): UnalignGate {
  const status = carePeriod?.status;
  if (!status) return NO_UNALIGN;
  if (status === 'active') return { visible: true, testReset: false };
  if (env === 'prod' || status === 'pending_unalignment') return NO_UNALIGN;
  return { visible: true, testReset: true };
}
